// src/pages/newspaper/NewspaperCategoryPage.jsx
import { useEffect, useRef,useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import RankingSliderIcon from '@/assets/all-icons/ranking-slider-icon';
import config from '@/config';
import FeaturedArticleCard from '@/shared-components/forms/add-board-content/shared-components/FeaturedArticleCard';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { assignLocalizedPublicPath } from '@/utils/localePublicUrl';

import './NewspaperCategoryPage.css';

export default function NewspaperCategoryPage() {
  const { collection_url } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [collection, setCollection] = useState(null);

  const [displayArticles, setDisplayArticles] = useState([]);

  //
  // — DESCRIPTION SLIDER STATE (arrowless, autoplay) —
  //
  const descLoop = collection?.collection_description_loop || [];
  const [lockedDesc, setLockedDesc] = useState(descLoop);
  const descCount = lockedDesc.length;

  const descTrackRef    = useRef(null);
  const descViewportRef = useRef(null);
  const [descIndex, setDescIndex]                     = useState(1);
  const [descTransitionEnabled, setDescTransitionEnabled] = useState(true);
  const prevDescCountRef = useRef(descCount);

  // keep lockedDesc in sync when the user-provided loop changes
  useEffect(() => {
    if (descLoop.length) setLockedDesc(descLoop);
  }, [descLoop]);

  // autoplay progression every 5s
  useEffect(() => {
    if (descCount === 0) return;
    if (prevDescCountRef.current !== descCount) {
      setDescIndex(1);
      prevDescCountRef.current = descCount;
    }
    const iv = setInterval(() => {
      setDescIndex(prev => {
        let next = prev + 1;
        if (next > descCount + 1) next = 1;
        return next;
      });
    }, 5000);
    return () => clearInterval(iv);
  }, [descCount]);

  // apply the CSS transform on the track
  useEffect(() => {
    if (descCount === 0) return;
    const track = descTrackRef.current;
    if (!track) return;
    track.style.transition = descTransitionEnabled ? 'transform 0.5s ease' : 'none';
    track.style.transform  = `translateX(-${descIndex * 100}%)`;
  }, [descIndex, descTransitionEnabled, descCount]);

  // when we hit a clone slide, snap to the real one
  const onDescTransitionEnd = () => {
    if (descIndex === 0) {
      setDescTransitionEnabled(false);
      setDescIndex(descCount);
    } else if (descIndex === descCount + 1) {
      setDescTransitionEnabled(false);
      setDescIndex(1);
    }
  };

  // re-enable transition after a snap
  useEffect(() => {
    if (!descTransitionEnabled) {
      requestAnimationFrame(() => setDescTransitionEnabled(true));
    }
  }, [descTransitionEnabled]);

  //
  // — FETCH COLLECTION —
  //
  useEffect(() => {
    async function fetchData() {
      try {
        const res  = await fetch(`${config.apis.api.url}/newspaper/collection/${collection_url}`);
        const json = await res.json();
        setCollection(json);
        setDisplayArticles(json.collection_articles || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [collection_url]);

  if (loading) {
    return (
      <div className="newspaper-category__loading">
        <Loader thin />
      </div>
    );
  }
  if (!collection) {
    return <p>Sorry, that category wasn’t found.</p>;
  }

  return (
    <>
      <StaticSeoHead
        title={`${collection.collection_name} | Suarte Newspaper`}
        description={collection.collection_description || `Explore ${collection.collection_name} on Suarte Newspaper.`}
        canonicalPath={`/newspaper/${collection_url}`}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Newspaper', url: 'https://suarte.art/newspaper' },
          { name: collection.collection_name, url: `https://suarte.art/newspaper/${collection_url}` },
        ]}
      />

      <AppNavigationPage>
        <div className="newspaper-category__container">
          <header className="newspaper-category__header">
            <BackArrowIcon
              className="newspaper-category__back-icon"
              onClick={() => navigate(-1)}
            />
            <h1>{collection.collection_name}</h1>
          </header>

          {descCount > 0 ? (
            <div className="newspaper-category__description-slider">
              <div
                className="newspaper-category__viewport"
                ref={descViewportRef}
              >
                <div
                  className="newspaper-category__track"
                  ref={descTrackRef}
                  onTransitionEnd={onDescTransitionEnd}
                >
                  {[
                    lockedDesc[descCount - 1],
                    ...lockedDesc,
                    lockedDesc[0],
                  ].map((text, i) => (
                    <div
                      key={i}
                      className="newspaper-category__slider-item"
                    >
                      <p className="newspaper-category__description-text">
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            collection.collection_description && (
              <p className="newspaper-category__description-text">
                {collection.collection_description}
              </p>
            )
          )}

          <section className="newspaper-category__list">
            {displayArticles.map((article, index) => {
              const card = (
                <FeaturedArticleCard
                  article={article}
                  onClickRead={() => assignLocalizedPublicPath(`/article/${article._id}`)}
                />
              );
              if (collection.type === 'newspaper-ranking') {
                return (
                  <div
                    key={article._id}
                    className="newspaper-category__list-item"
                  >
                    <div className="article-ranking-slider__order">
                      <RankingSliderIcon className="article-ranking-slider__order-icon" />
                      <span className="article-ranking-slider__order-number">
                        {index + 1}
                      </span>
                    </div>
                    {card}
                  </div>
                );
              }
              return (
                <FeaturedArticleCard
                  key={article._id}
                  article={article}
                  onClickRead={() => assignLocalizedPublicPath(`/article/${article._id}`)}
                />
              );
            })}
          </section>
        </div>
      </AppNavigationPage>
    </>
  );
}
